/**
 * WelcomePopup — iter251 first-visitor hook
 *
 * Triggers on:
 *   1. First-ever visit (no `wh_shown_v1` localStorage flag), 4s after page load.
 *   2. OR exit-intent on desktop (mouse leaving via the top of the viewport).
 *
 * Captures email (+ optional phone) → POSTs to /api/marketing/visitor-hook
 * → backend mints a single-use coupon → frontend shows the code + a
 * "copy + use now" CTA.
 *
 * Honours `visitor_hook_enabled` admin flag (via /api/marketing/visitor-hook/status).
 * If disabled, the component never mounts the modal — zero perf impact.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Gift, Check, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLAG_SHOWN = "wh_shown_v1";          // shown this session
const FLAG_DISMISSED = "wh_dismissed_v1";  // user closed without redeeming
const FLAG_REDEEMED = "wh_redeemed_v1";    // got a coupon

export default function WelcomePopup() {
    const [status, setStatus] = useState(null);   // { enabled, heading, subheading, ... }
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [phase, setPhase] = useState("form");   // "form" | "submitting" | "success"
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const exitIntentBound = useRef(false);

    // 1️⃣ Fetch hook status on mount
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/marketing/visitor-hook/status`)
            .then((r) => {
                if (!cancelled) setStatus(r?.data || null);
            })
            .catch(() => { /* silent — don't bother customer if config fetch fails */ });
        return () => { cancelled = true; };
    }, []);

    // 2️⃣ Decide if/when to open
    useEffect(() => {
        if (!status?.enabled) return;
        // Don't show if customer already redeemed or dismissed within the
        // dismiss window (24h).
        if (typeof window === "undefined") return;
        const redeemed = localStorage.getItem(FLAG_REDEEMED);
        if (redeemed) return;

        const dismissedAt = parseInt(localStorage.getItem(FLAG_DISMISSED) || "0", 10);
        if (dismissedAt && Date.now() - dismissedAt < 24 * 60 * 60 * 1000) return;

        // Already shown this tab session? skip
        if (sessionStorage.getItem(FLAG_SHOWN)) return;

        // First-visit timer (4s) + exit-intent (desktop)
        const t = setTimeout(() => {
            sessionStorage.setItem(FLAG_SHOWN, "1");
            setOpen(true);
        }, 4000);

        // Exit-intent (desktop only — touch devices don't have mouseleave-via-top)
        if (window.matchMedia("(pointer: fine)").matches && !exitIntentBound.current) {
            exitIntentBound.current = true;
            const onMove = (e) => {
                if (e.clientY <= 5 && !sessionStorage.getItem(FLAG_SHOWN)) {
                    sessionStorage.setItem(FLAG_SHOWN, "1");
                    setOpen(true);
                    clearTimeout(t);
                }
            };
            document.addEventListener("mouseout", onMove);
            return () => {
                clearTimeout(t);
                document.removeEventListener("mouseout", onMove);
            };
        }
        return () => clearTimeout(t);
    }, [status]);

    const handleClose = () => {
        setOpen(false);
        try {
            localStorage.setItem(FLAG_DISMISSED, String(Date.now()));
        } catch (_) { /* ignore */ }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
            setError("Please enter a valid email.");
            return;
        }
        setError("");
        setPhase("submitting");
        try {
            const r = await axios.post(`${API}/marketing/visitor-hook`, {
                email: email.trim(),
                phone: phone.trim() || null,
            });
            setResult(r.data);
            setPhase("success");
            try {
                localStorage.setItem(FLAG_REDEEMED, "1");
            } catch (_) { /* ignore */ }
        } catch (err) {
            const msg = err?.response?.data?.detail || "Couldn't save right now — please try again in a moment.";
            setError(msg);
            setPhase("form");
        }
    };

    const copyCode = () => {
        if (!result?.coupon_code) return;
        navigator.clipboard?.writeText(result.coupon_code)
            .then(() => toast.success(`Copied ${result.coupon_code}`))
            .catch(() => toast.message(result.coupon_code));
    };

    if (!open || !status?.enabled) return null;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-6 animate-fade-in"
            data-testid="welcome-popup"
            data-nosnippet
            onClick={handleClose}
        >
            <div
                className="bg-ivory w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl border border-rosegold/30 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header strip */}
                <div className="relative h-32 sm:h-36 bg-gradient-to-br from-rosegold via-wine to-ink flex items-center justify-center">
                    <Gift size={42} className="text-ivory" />
                    <button
                        type="button"
                        onClick={handleClose}
                        data-testid="welcome-popup-close"
                        aria-label="Close welcome offer"
                        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-ivory/20 hover:bg-ivory/40 text-ivory flex items-center justify-center transition"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 sm:p-6 space-y-4">
                    {phase !== "success" ? (
                        <>
                            <div className="text-center">
                                <p className="text-[10px] uppercase tracking-[0.24em] text-rosegold font-bold">First-visit offer</p>
                                <h2 className="font-heading text-2xl sm:text-3xl text-ink mt-1">{status.heading}</h2>
                                <p className="text-sm text-stone-600 mt-2 leading-relaxed">{status.subheading}</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4 pt-2">
                                {/* Email field — matches the AuthOptionsModal pattern */}
                                <div>
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-ink-soft block mb-1.5">
                                        Email
                                    </label>
                                    <div className="relative">
                                        <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
                                        <input
                                            type="email"
                                            inputMode="email"
                                            autoComplete="email"
                                            placeholder="you@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            data-testid="welcome-popup-email"
                                            required
                                            autoFocus
                                            className="w-full h-11 pl-10 pr-3 rounded-md border-2 border-stone-300 bg-white text-sm focus:outline-none focus:border-rosegold focus:ring-2 focus:ring-rosegold/20 transition-all"
                                        />
                                    </div>
                                </div>

                                {/* Phone field with +91 prefix — matches AuthOptionsModal */}
                                <div>
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-ink-soft block mb-1.5">
                                        Phone <span className="text-ink-soft/70 font-normal normal-case">(optional, for WhatsApp updates)</span>
                                    </label>
                                    <div className="flex items-stretch gap-2">
                                        <div className="inline-flex items-center px-3 h-11 rounded-md border-2 border-stone-300 bg-stone-50 text-sm font-semibold text-ink">+91</div>
                                        <div className="relative flex-1">
                                            <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
                                            <input
                                                type="tel"
                                                inputMode="numeric"
                                                autoComplete="tel"
                                                maxLength={10}
                                                placeholder="98765 43210"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                                data-testid="welcome-popup-phone"
                                                className="w-full h-11 pl-10 pr-3 rounded-md border-2 border-stone-300 bg-white text-sm focus:outline-none focus:border-rosegold focus:ring-2 focus:ring-rosegold/20 transition-all"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {error && (
                                    <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
                                )}

                                <button
                                    type="submit"
                                    disabled={phase === "submitting"}
                                    data-testid="welcome-popup-submit"
                                    className="w-full h-11 rounded-md bg-ink text-ivory font-bold text-sm tracking-wide hover:bg-rosegold transition disabled:opacity-50"
                                >
                                    {phase === "submitting" ? "Sending your code..." : `Unlock ₹${status.coupon_amount} off`}
                                </button>

                                <p className="text-[10px] text-stone-400 text-center pt-1 leading-relaxed">
                                    Valid on orders above ₹{status.min_order.toLocaleString()} ·
                                    Single-use · By signing up you agree to receive boutique updates
                                </p>
                            </form>
                        </>
                    ) : (
                        <div className="text-center space-y-4 py-2">
                            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                                <Check size={28} className="text-emerald-700" />
                            </div>
                            <div>
                                <h2 className="font-heading text-2xl text-ink">Your code is ready ✨</h2>
                                <p className="text-sm text-stone-600 mt-2">
                                    We&apos;ve also emailed it to <span className="font-semibold">{email}</span> for safekeeping.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={copyCode}
                                data-testid="welcome-popup-copy"
                                className="w-full py-4 rounded-2xl border-2 border-dashed border-rosegold bg-rosegold/5 hover:bg-rosegold/15 transition"
                            >
                                <p className="text-[10px] uppercase tracking-[0.22em] text-rosegold font-bold">Tap to copy</p>
                                <p className="font-mono text-2xl font-bold text-ink mt-1">{result?.coupon_code}</p>
                            </button>

                            <p className="text-xs text-stone-500">
                                ₹{result?.discount_amount} off any order above ₹{result?.min_order?.toLocaleString()} ·
                                Valid for {result?.validity_days} days
                            </p>

                            <button
                                type="button"
                                onClick={handleClose}
                                data-testid="welcome-popup-shop-now"
                                className="w-full h-11 rounded-md bg-ink text-ivory font-bold text-sm tracking-wide hover:bg-rosegold transition"
                            >
                                Start shopping
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
